import React, { useEffect, useState } from "react";
import { Image, ScrollView, Text, TouchableOpacity, View } from "react-native";
import { LinearGradient } from "expo-linear-gradient";
import {
  CreateResponsiveStyle,
  DEVICE_SIZES,
  useDeviceSize,
} from "rn-responsive-styles";
import { AppColors } from "../../utility/AppColors";
import ImageComponent from "../ImageComponent";
import { AppImages } from "../../utility/AppImages";
import { AppFontStyle } from "../../styles/AppFontStyle";
import { StackActions, useNavigation } from "@react-navigation/native";
import { navigateToScreen } from "../../utility/NavigationHelper";
import Separator from "../Separator";
import { CropImage } from "../choose-avatar-components/CropImage";

import { toast } from "react-toastify";
import UserDetailSkeleton from "../skeleton-components/UserDetailSkeleton";
import AvatarWalkthrough from "../walk-through-common-components/profileTooltips/AvatarWalkthrough";
import BioWalkthrough from "../walk-through-common-components/profileTooltips/BioWalkthrough";
import { AppScreens } from "../../screens/Utility/AppScreens";
import Avatar from "../choose-avatar-components/Avatar";

interface SocialLink {
  instagram?: string;
  youtube?: string;
  twitch?: string;
  discord?: string;
  x?: string;
  linkedIn?: string;
  facebook?: string;
}

interface UserProfileData {
  profilePicture?: string;
  userID?: string;
  bio?: string;
}

interface UserDetailProps {
  setAddLinkPopup: (value: boolean) => void;
  userProfileData: UserProfileData;
  links: SocialLink;
  isEditable?: boolean;
  isLoading?: boolean;
  scrollViewRef?: any;
  setActiveTab?: any;
}

const UserDetail: React.FC<UserDetailProps> = ({
  setAddLinkPopup,
  userProfileData,
  links,
  isEditable = true,
  isLoading = false,
  scrollViewRef = null,
  setActiveTab = 0,
}) => {
  const styles = useStyles();
  const deviceSize = useDeviceSize();
  const navigation = useNavigation();

  const socialMediaData = [
    links.instagram && {
      name: "Instagram",
      image: AppImages.icSocialInsta,
      link: links.instagram,
    },
    links.youtube && {
      name: "YouTube",
      image: AppImages.icSocialYoutube,
      link: links.youtube,
    },
    links.twitch && {
      name: "Twitch",
      image: AppImages.icSocialTwitch,
      link: links.twitch,
    },
    links.discord && {
      name: "Discord",
      image: AppImages.icSocialDiscord,
      link: links.discord,
    },
    links.x && { name: "X", image: AppImages.icSocialX, link: links.x },
    links.linkedIn && {
      name: "LinkedIn",
      image: AppImages.icLinkedIn,
      link: links.linkedIn,
    },
    links.facebook && {
      name: "Facebook",
      image: AppImages.icSocialFacebook,
      link: links.facebook,
    },
  ].filter(Boolean);

  if (isLoading) {
    return <UserDetailSkeleton />;
  }

  return (
    <View style={{ flex: 1 }}>
      <LinearGradient
        colors={["rgba(31, 41, 55, 0.50)", "rgba(17, 24, 39, 0.50)"]}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
        style={{
          width:
            deviceSize === DEVICE_SIZES.SM || deviceSize === DEVICE_SIZES.XS
              ? "100%"
              : 347,
          padding: 16,
          borderColor: "#374151",
          borderWidth: 0.8,
          borderRadius: 8,
          gap: 12,
          flex: 1,
        }}
      >
        {/* Avatar and Real Image Row */}
        <View
          style={{
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
            alignSelf: "stretch",
          }}
        >
          <AvatarWalkthrough
            content={
              <View
                style={{
                  width: 96.251,
                  height: 96.251,
                  borderRadius: 64.913,
                  borderColor: "#D2D2D2",
                  borderWidth: 0.746,
                  justifyContent: "center",
                  alignItems: "center",
                  overflow: "hidden",
                }}
              >
                <Image
                  source={{ uri: userProfileData?.profilePicture }}
                  style={{
                    width: "100%",
                    height: "100%",
                    resizeMode: "contain",
                  }}
                />
                {isEditable && (
                  <TouchableOpacity
                    style={{
                      position: "absolute",
                      padding: 6,
                      right: -5,
                      borderRadius: 57,
                      backgroundColor: AppColors.gray_525252,
                    }}
                    onPress={() => {
                      navigateToScreen(navigation, AppScreens.ChangeAvatar, {
                        screen: "Profile",
                      });
                    }}
                  >
                    <ImageComponent
                      url={AppImages.icImageEdit}
                      style={[
                        {
                          resizeMode: "contain",
                          width: 18,
                          height: 18,
                        },
                      ]}
                    />
                  </TouchableOpacity>
                )}
              </View>
            }
          />
          {/* Real Image Upload */}
          <View
            style={{
              width: 96.251,
              height: 96.251,
              borderRadius: 64.913,
              borderColor: "#D2D2D2",
              borderWidth: 0.746,
              justifyContent: "center",
              alignItems: "center",
              backgroundColor: AppColors.appBlackColor_1e1e1e,
              overflow: "hidden",
            }}
          >
            {/* Placeholder for real image - will be implemented */}
            {isEditable && (
              <TouchableOpacity
                style={{
                  position: "absolute",
                  padding: 6,
                  right: -5,
                  borderRadius: 57,
                  backgroundColor: AppColors.gray_525252,
                }}
                onPress={() => {
                  // TODO: Implement real image upload
                  toast.info("Real image upload coming soon");
                }}
              >
                <ImageComponent
                  url={AppImages.icImageEdit}
                  style={[
                    {
                      resizeMode: "contain",
                      width: 18,
                      height: 18,
                    },
                  ]}
                />
              </TouchableOpacity>
            )}
            <Image
              source={AppImages.icCamera}
              style={{
                width: 32,
                height: 32,
                resizeMode: "contain",
                tintColor: AppColors.gray_828282,
              }}
            />
          </View>
        </View>

        {/* Username */}
        <Text style={AppFontStyle.BOLD_ANYBODY_24}>
          {userProfileData?.userID}
        </Text>

        {/* Bio Section */}
        <BioWalkthrough
          scrollViewRef={scrollViewRef}
          setActiveTab={setActiveTab}
          content={
            <View
              style={{
                borderRadius: 4,
                backgroundColor: "#121212",
                padding: 4,
                alignSelf: "stretch",
                position: "relative",
                marginTop: 1,
              }}
            >
              {isEditable && (
                <TouchableOpacity
                  style={{
                    position: "absolute",
                    top: 4,
                    right: 4,
                    zIndex: 10,
                  }}
                  onPress={() => {
                    navigateToScreen(navigation, AppScreens.SettingWebScreen);
                  }}
                >
                  <Image
                    source={AppImages.icEdit}
                    style={{
                      height: 16,
                      width: 16,
                    }}
                  />
                </TouchableOpacity>
              )}
              <ScrollView
                style={{
                  maxHeight: 70,
                }}
                contentContainerStyle={{
                  flexGrow: 1,
                }}
                showsHorizontalScrollIndicator={false}
              >
                <Text
                  style={[
                    AppFontStyle.REGULAR_ANYBODY_14,
                    {
                      width: "100%",
                      minHeight: 40,
                      padding: 8,
                      paddingRight: 28,
                    },
                  ]}
                >
                  {userProfileData?.bio || "Add Bio"}
                </Text>
              </ScrollView>
            </View>
          }
        />
        <Separator
          color="#374151"
          height={1}
          marginTop={0}
          marginBottom={0}
          marginHorizontal={0}
        />

        {/* Social Links */}
        <View
          style={{
            paddingHorizontal: 10,
            paddingVertical: 4,
            alignSelf: "stretch",
          }}
        >
          <View
            style={{
              flexDirection: "row",
              flexWrap: "wrap",
              gap: 16,
              alignItems: "center",
            }}
          >
            {socialMediaData.map(
              (item, index) =>
                item && (
                  <TouchableOpacity
                    key={index}
                    onPress={() => window.open(item.link, "_blank")}
                  >
                    <View
                      style={{
                        flexDirection: "column",
                        gap: 8,
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <View
                        style={{
                          width: 40,
                          height: 40,
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                      >
                        <ImageComponent
                          url={item?.image}
                          style={[
                            {
                              resizeMode: "contain",
                              width: 36,
                              height: 36,
                            },
                          ]}
                        />
                      </View>
                      <Text
                        style={[
                          AppFontStyle.REGULAR_ANYBODY_12,
                          { textAlign: "center" },
                        ]}
                      >
                        {item.name}
                      </Text>
                    </View>
                  </TouchableOpacity>
                )
            )}
            {socialMediaData.length === 0 && (
              <Text style={AppFontStyle.REGULAR_ANYBODY_12}>No Links Added</Text>
            )}
          </View>
        </View>

        {/* Add Social Link Button */}
        {isEditable && (
          <TouchableOpacity
            onPress={() => setAddLinkPopup(true)}
            style={{ alignSelf: "flex-end", paddingHorizontal: 10, paddingTop: 4 }}
          >
            <Text style={[AppFontStyle.BOLD_ANYBODY_12, { textAlign: "right" }]}>
              Add social link
            </Text>
          </TouchableOpacity>
        )}
      </LinearGradient>
    </View>
  );
};

export default UserDetail;

const useStyles = CreateResponsiveStyle(
  {
    profileImg: {
      width: 200,
      height: 196,
      aspectRatio: 1,
      borderRadius: 12,
    },
    beltArea: {
      width: 493,
    },
    searchTextStyle: {
      backgroundColor: AppColors.appBlackColor_1e1e1e,
      borderWidth: 0,
      borderRadius: 10,
      padding: 12,
      verticalAlign: "top",
      width: "100%",
    },
    inputSearch: {
      height: "100%",
    },
  },
  {
    [DEVICE_SIZES.MD]: {
      beltArea: {
        width: 325,
      },
    },
    [DEVICE_SIZES.LG]: {
      beltArea: {
        width: 400,
      },
    },
    [DEVICE_SIZES.SM]: {
      profileImg: {
        width: 128,
        height: 126,
      },
      beltArea: {
        width: 230,
      },
      searchTextStyle: { width: "100%" },
    },
    [DEVICE_SIZES.XS]: {
      profileImg: {
        width: 128,
        height: 126,
      },
      beltArea: {
        width: 230,
      },
      searchTextStyle: { width: "100%" },
    },
  }
);
